import { memo, useEffect } from "react"
import {
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  ListSubheader,
  FormControl,
  FormHelperText,
} from "@mui/material"
import Tooltip from "components/UI/Tooltip"
import { styles } from "./SelectInput.styles"

const buildMenuItems = (options, renderOption, stylesRef) => {
  const hasGroups = options?.some(
    (o) => typeof o === "object" && o !== null && o.group != null
  )

  if (!hasGroups) {
    return options.map((option) => {
      const optValue =
        typeof option === "string" ? option : option.id ?? option.value
      const optLabel =
        typeof option === "string" ? option : option.label ?? option.name
      return (
        <MenuItem key={optValue} value={optValue}>
          {renderOption ? (
            renderOption(optValue, options)
          ) : (
            <Typography variant="body2" sx={stylesRef.menuItemText}>
              {optLabel}
            </Typography>
          )}
        </MenuItem>
      )
    })
  }

  // Grouped rendering with ListSubheader — preserves insertion order
  const seenGroups = new Set()
  const items = []
  options.forEach((option) => {
    const optValue =
      typeof option === "string" ? option : option.id ?? option.value
    const optLabel =
      typeof option === "string" ? option : option.label ?? option.name
    const groupName = option.group ?? ""

    if (groupName && !seenGroups.has(groupName)) {
      seenGroups.add(groupName)
      items.push(
        <ListSubheader key={`group-${groupName}`} sx={stylesRef.groupHeader}>
          {groupName}
        </ListSubheader>
      )
    }

    items.push(
      <MenuItem key={optValue} value={optValue}>
        {renderOption ? (
          renderOption(optValue, options)
        ) : (
          <Typography variant="body2" sx={stylesRef.menuItemText}>
            {optLabel}
          </Typography>
        )}
      </MenuItem>
    )
  })
  return items
}

export const SelectInput = memo(
  ({
    value,
    options = [],
    placeholder,
    label,
    startAdornment,
    renderOption,
    onChange,
    disabled = false,
    isLoading = false,
    autoSelectSingle = false,
    multiple = false,
    sx = {},
    fullWidth = false,
    error,
    helperText,
    ...props
  }) => {
    useEffect(() => {
      if (
        autoSelectSingle &&
        !value &&
        options.length === 1 &&
        typeof onChange === "function"
      ) {
        const singleOption = options[0]
        const singleVal =
          typeof singleOption === "string"
            ? singleOption
            : singleOption.id ?? singleOption.value
        onChange(singleVal)
      }
    }, [autoSelectSingle, value, options, onChange])

    const select = (
      <Select
        value={multiple ? (Array.isArray(value) ? value : []) : value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        multiple={multiple}
        displayEmpty
        size="small"
        fullWidth={fullWidth}
        disabled={disabled || isLoading}
        startAdornment={startAdornment}
        endAdornment={
          <Box
            sx={[
              styles.loadingIndicator,
              !isLoading && styles.loadingIndicatorHidden,
            ]}
          >
            <CircularProgress size={16} color="inherit" />
          </Box>
        }
        MenuProps={styles.menuProps}
        sx={[
          styles.select,
          label && styles.selectWithLabel,
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        renderValue={(selected) => {
          const isEmpty = Array.isArray(selected)
            ? selected.length === 0
            : !selected
          if (isEmpty && placeholder) {
            return (
              <Typography variant="body2" sx={styles.placeholder}>
                {placeholder}
              </Typography>
            )
          }
          if (renderOption) {
            if (Array.isArray(selected)) {
              const displayedSelected = selected.slice(0, 2)
              const hasMore = selected.length > 2
              return (
                <Box sx={styles.multiRenderRow}>
                  {displayedSelected.map((val, index) => (
                    <Box
                      key={val}
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        minWidth: 0,
                        flexShrink: index === 0 ? 0 : 1,
                      }}
                    >
                      {renderOption(val, options, true)}
                      {index < displayedSelected.length - 1 && (
                        <Typography variant="body2" sx={{ mr: 0.5 }}>
                          ,
                        </Typography>
                      )}
                    </Box>
                  ))}
                  {hasMore && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", ml: 0.5 }}
                    >
                      ...
                    </Typography>
                  )}
                </Box>
              )
            }
            return renderOption(selected, options, true)
          }
          if (Array.isArray(selected)) {
            const displayedSelected = selected.slice(0, 2)
            const hasMore = selected.length > 2
            const labels = displayedSelected.map((val) => {
              const opt = options.find(
                (o) =>
                  String(typeof o === "string" ? o : o.id ?? o.value) ===
                  String(val)
              )
              return opt
                ? typeof opt === "string"
                  ? opt
                  : opt.label ?? opt.name
                : val
            })
            return (
              <Typography variant="body2" sx={styles.valueText}>
                {labels.join(", ") + (hasMore ? ", ..." : "")}
              </Typography>
            )
          }
          const option = options.find(
            (o) =>
              String(typeof o === "string" ? o : o.id ?? o.value) ===
              String(selected)
          )
          const valueLabel = option
            ? typeof option === "string"
              ? option
              : option.label ?? option.name
            : selected
          const selectValue = (
            <Tooltip
              title={valueLabel}
              enterDelay={300}
              arrow
              divStyle={styles.tooltipDiv}
            >
              <Typography variant="body2" sx={styles.valueText}>
                {valueLabel}
              </Typography>
            </Tooltip>
          )
          return label ? (
            <Box sx={styles.labelValueStack}>
              <Typography variant="caption" sx={styles.selectLabel}>
                {label}
              </Typography>
              {selectValue}
            </Box>
          ) : (
            selectValue
          )
        }}
        {...props}
      >
        {buildMenuItems(options, renderOption, styles)}
      </Select>
    )

    const hasFeedback = error !== undefined || helperText !== undefined
    if (!hasFeedback) return select

    return (
      <FormControl fullWidth={fullWidth} error={Boolean(error)}>
        {select}
        <FormHelperText sx={styles.helperText}>
          {helperText || "\u00A0"}
        </FormHelperText>
      </FormControl>
    )
  }
)

export default SelectInput
